'use client';

import { BottomDrawer, Button, Checkbox } from '@common/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  AGREEMENT_TYPE,
  AuthApi,
  AuthQueryOptions,
  TERM_CATEGORY,
} from '@/api';
import { ErrorFallback } from '@/app/(allowed)/(home)/_components/ErrorFallback';
import { ROUTES, SEARCH_PARAMS, SELECTOR, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useGlobalStore } from '@/stores/GlobalStore';

import { LoadingPageTemplate } from './LoadingPageTemplate';

export const PolicyFilterBottomDrawer = () => {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<></>}>
        <PolicyFilterBottomDrawerSuspense />
      </Suspense>
    </ErrorBoundary>
  );
};

const PolicyFilterBottomDrawerSuspense = () => {
  const [isPrivateChecked, setIsPrivateChecked] = useState(false);
  const [isSensitiveChecked, setIsSensitiveChecked] = useState(false);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const policyFilter = searchParams?.get(SEARCH_PARAMS.POLICY_FILTER);

  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  const privateDataConsentQuery = useQuery({
    ...AuthQueryOptions[
      'get:/wello/v2/auth/member-wello/private_data_consent'
    ](),
    select: (data) => data.context?.privateDataConsentYN ?? false,
  });

  const { data } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member-term-agreement']({
      query: {
        agreementTypeCd: AGREEMENT_TYPE.SENSITIVE_INFO_USE_POLICY_SELECT,
        termCategoryId: TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY,
      },
    }),
  });

  const isPrivateDataConsentChecked = privateDataConsentQuery.data;
  const isSensitiveInfoUsePolicyChecked = data?.context?.agreeYN ?? false;
  const isPolicyFilterOpened =
    !isSensitiveInfoUsePolicyChecked && policyFilter === 'true';

  const privateDataConsentMutation = useMutation({
    mutationFn: (isAgree: boolean) =>
      AuthApi['put:/wello/v2/auth/member-wello/private_data_consent']({
        body: {
          privateDataConsentYN: isAgree,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const {
    mutate: sensitiveInfoUsePolicyMutation,
    isPending: isSensitiveInfoUsePolicyPending,
  } = useMutation({
    mutationFn: ({ isChecked }: { isChecked: boolean }) =>
      AuthApi['post:/wello/v2/auth/member-term-agreement']({
        body: {
          agreeYN: isChecked,
          agreementTypeCd: AGREEMENT_TYPE.SENSITIVE_INFO_USE_POLICY_SELECT,
          termCategoryId: TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const router = useCustomRouter();

  const handleSubmit = () => {
    privateDataConsentMutation.mutate(true);

    sensitiveInfoUsePolicyMutation({
      isChecked: isPrivateDataConsentChecked ? true : isSensitiveChecked,
    });

    const url = new URL(window.location.href);

    url.searchParams.delete(SEARCH_PARAMS.POLICY_FILTER);

    history.replaceState(null, '', url.search);
  };

  const isLoading =
    privateDataConsentMutation.isPending ||
    privateDataConsentQuery.isLoading ||
    isSensitiveInfoUsePolicyPending;

  return privateDataConsentQuery.isFetched ? (
    <BottomDrawer
      opened={isPolicyFilterOpened || !(isPrivateDataConsentChecked ?? true)}
      renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}
    >
      <BottomDrawer.HandleWrapper>
        <BottomDrawer.Handle />
      </BottomDrawer.HandleWrapper>
      <BottomDrawer.Contents className="flex flex-col pb-16">
        <h2 className="mb-8 font-700 text-Gray-900 typo-body2">
          정책 추천을 위해 동의해주세요!
        </h2>
        <p className="mb-16 text-Gray-700 typo-body4">
          동의하시면 맞춤정책을 추천 받을 수 있어요.
        </p>
        {isPrivateDataConsentChecked ? null : (
          <p className="text-Gray-black typo-body4">
            <Checkbox
              checked={isPrivateDataConsentChecked || isPrivateChecked}
              onCheckedChange={() => {
                setIsPrivateChecked(!isPrivateChecked);
              }}
            >
              <span className="font-500">(필수)</span> 개인정보 수집 및 이용
              동의
              <button
                className="ml-4 font-600 underline"
                type="button"
                onClick={() =>
                  setTermsDrawerType(
                    TERM_CATEGORY.FIT_PRIVACY_USE_POLICY_SELECT,
                  )
                }
              >
                보기
              </button>
            </Checkbox>
          </p>
        )}
        <p className="mb-28 text-Gray-black typo-body4">
          <Checkbox
            checked={isSensitiveChecked || isSensitiveInfoUsePolicyChecked}
            onCheckedChange={() => {
              setIsSensitiveChecked(!isSensitiveChecked);
            }}
          >
            <span className="font-500">(선택)</span> 민감정보 수집 및 이용 동의
            <button
              className="ml-4 font-600 underline"
              type="button"
              onClick={() =>
                setTermsDrawerType(TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY)
              }
            >
              보기
            </button>
          </Checkbox>
        </p>
        <Button
          disabled={!(isPrivateChecked || isPrivateDataConsentChecked)}
          loading={isLoading}
          onClick={handleSubmit}
        >
          동의하기
        </Button>
        <Button
          loading={isLoading}
          theme="sub-text"
          onClick={async () => {
            if (isPrivateDataConsentChecked) {
              const url = new URL(window.location.href);

              url.searchParams.delete(SEARCH_PARAMS.POLICY_FILTER);

              history.replaceState(null, '', url.search);

              return;
            }

            await privateDataConsentMutation.mutateAsync(false);

            const policyFilterLandingFrom = sessionStorage.getItem(
              STORAGE_KEY.POLICY_FILTER_LANDING_FROM,
            );

            sessionStorage.removeItem(STORAGE_KEY.POLICY_FILTER_LANDING_FROM);

            router.push(policyFilterLandingFrom || ROUTES.HOME.pathname);
          }}
        >
          다음에 하기
        </Button>
      </BottomDrawer.Contents>
    </BottomDrawer>
  ) : (
    <LoadingPageTemplate />
  );
};
